import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Badge, Button, EditPane, toast, useConfirm, type EditPaneTab } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AssemblyVersionClient,
  ManufacturingPartClient,
  PartEdgebandClient,
  PartMeasurementClient,
  PartSupplierClient,
  PartUnitClient,
  SupplierClient,
  dimensionLabel,
  refusalMessage,
  type AttributeDTO,
  type AttributeOptionDTO,
  type DepartmentDTO,
  type MeasurementKindDTO,
  type OperationDTO,
  type PartAttributeGroupDTO,
  type PartDTO,
  type PartGroupDTO,
  type UnitDTO,
} from "../../api/manufacturing";
import { NexpimView } from "../../views";
import { PartColoursTab } from "../configurator/PartColoursTab";
import { PartBomTab } from "./PartBomTab";
import { VirtualPartRealParts } from "../config/VirtualPartsPage";
import { PartFilesPanel } from "./PartFilesPanel";
import { partDraftInvalid, partStatusLabel, partTypeLabel, PartGeneralTab } from "./PartGeneralTab";
import { PartNotesTab } from "./PartNotesTab";
import { PartPurchasesTab } from "./PartPurchasesTab";
import { PartReplenishmentTab } from "./PartReplenishmentTab";
import { PartStockHeadline, PartStockTab } from "./PartStockTab";
import { PartStockedAtTab } from "./PartStockedAtTab";
import { PartSubcontractTab } from "./PartSubcontractTab";
import { PartWhereUsedTab } from "./PartWhereUsedTab";
import {
  AttributesTab,
  EdgebandsTab,
  MeasurementsTab,
  SuppliersTab,
  UnitsTab,
} from "./PartRelatedTabs";

// The Parts list's server table and the part pickers: both show what a save
// here changes (number, name, group, on hand).
const PARTS_QUERY = ["manufacturing", "part-grid"] as const;
const PICKER_QUERY = ["manufacturing", "part-picker"] as const;

/**
 * One part, with the collections that hang off it.
 *
 * <p>The part record is a buffered draft — every field edits it and nothing
 * reaches the server until Save, the same model the product page uses. The
 * related rows are not buffered: each is its own resource with its own
 * endpoint, so the inline editor saves a row the moment it is confirmed and
 * the detail is refetched. Mixing the two would mean a Save button that
 * sometimes writes six tables and sometimes one.</p>
 */
export function ManufacturingPartDetailPage({ partId }: { partId: number }) {
  const { t } = useTranslation();
  const { http, canWrite, hasView } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const allowed = hasView(NexpimView.MANUFACTURING_PARTS);

  const parts = useMemo(() => new ManufacturingPartClient(http), [http]);
  const measurementClient = useMemo(() => new PartMeasurementClient(http), [http]);
  const edgebandClient = useMemo(() => new PartEdgebandClient(http), [http]);
  const unitClient = useMemo(() => new PartUnitClient(http), [http]);
  const supplierLinkClient = useMemo(() => new PartSupplierClient(http), [http]);
  const supplierClient = useMemo(() => new SupplierClient(http), [http]);

  const detailKey = useMemo(() => ["manufacturing", "part", "detail", partId] as const, [partId]);

  const { data, isLoading, error } = useQuery({
    queryKey: detailKey,
    queryFn: () => parts.detail(partId),
    enabled: allowed,
    staleTime: 60_000,
  });

  // Reference lists: small, shared by every part, and needed by the pickers
  // before the first edit rather than after it.
  const reference = useReference(http, allowed);
  const { data: suppliers } = useQuery({
    queryKey: ["supplier", "all"],
    queryFn: () => supplierClient.findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });

  // The product this part belongs to (0066): as a version's output ("Sells as") or as its semi-finished.
  const { data: structure } = useQuery({
    queryKey: ["manufacturing", "product-structure", "part", partId],
    queryFn: () => new AssemblyVersionClient(http).ofPart(partId),
    enabled: allowed,
    staleTime: 60_000,
  });
  const version = structure?.versions.find((row) => row.output_part_id === partId) ?? null;
  const semiFinishedOf = structure && structure.semi_finished_part_id === partId ? structure : null;

  const [draft, setDraft] = useState<PartDTO | null>(null);
  // Keyed on the part, not the whole detail: saving a measurement or a
  // supplier refetches the detail, and that must not wipe edits made on the
  // General tab. Structural sharing keeps `data.part` the same object until
  // the part itself changes.
  const savedPart = data?.part;
  useEffect(() => {
    if (savedPart) setDraft(structuredClone(savedPart));
  }, [savedPart]);

  const dirty = useMemo(
    () => Boolean(draft && data && JSON.stringify(draft) !== JSON.stringify(data.part)),
    [draft, data],
  );
  // A blank number or name, or a figure the General tab flags: Save stays off
  // rather than letting the server refuse it.
  const incomplete = Boolean(draft && partDraftInvalid(draft));

  const save = useMutation({
    mutationFn: (part: PartDTO) => parts.update({ ...part, number: part.number.trim(), name: part.name.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: detailKey });
      await queryClient.invalidateQueries({ queryKey: PARTS_QUERY });
      await queryClient.invalidateQueries({ queryKey: PICKER_QUERY });
      toast.success(t("notification.saved", "Saved"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Save failed"))),
  });

  /**
   * Writes one related row and refetches the detail.
   *
   * Shared by all four collections because they differ only in which client
   * they use: every one of them is an `AController` resource with the same
   * create/update/delete surface, which is the whole point of the base class.
   */
  const writeRelated = <T extends { id: number }>(client: ResourceClient<T>) => ({
    onSave: async (row: T, isNew: boolean) => {
      if (isNew) {
        await client.create(row);
      } else {
        await client.update(row);
      }
      await queryClient.invalidateQueries({ queryKey: detailKey });
      await queryClient.invalidateQueries({ queryKey: PARTS_QUERY });
    },
    onDelete: async (row: T) => {
      await client.remove(row.id);
      await queryClient.invalidateQueries({ queryKey: detailKey });
      await queryClient.invalidateQueries({ queryKey: PARTS_QUERY });
    },
  });

  const leave = async (): Promise<boolean> => {
    if (!dirty) return true;
    return confirm({
      title: t("part.leave.title", "Discard unsaved changes?"),
      message: t("part.leave.message", "Your edits to this part have not been saved."),
      variant: "danger",
      confirmLabel: t("part.leave.discard", "Discard"),
    });
  };

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.manufacturing-parts", "Parts")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  if (isLoading || !draft || !data) {
    return (
      <>
        <PageHeader title={t("navigation.item.manufacturing-parts", "Parts")} />
        {error ? (
          <div className="p-4 lg:p-6">
            <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
              {t("part.not-found", "That part could not be loaded.")}
            </p>
          </div>
        ) : (
          <div className="flex h-40 items-center justify-center">
            <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          </div>
        )}
      </>
    );
  }

  const readOnly = !canWrite;
  const stockUnit = (reference.units ?? []).find((unit) => unit.id === draft.stock_unit_id);
  // Stock is counted in the saved unit; an unsaved pick on General does not
  // convert the balances.
  const savedStockUnit = (reference.units ?? []).find((unit) => unit.id === data.part.stock_unit_id);
  const group = (reference.groups ?? []).find((entry) => entry.id === draft.part_group_id);

  const size = dimensionLabel({
    length_mm: measurementBase(data, reference.kinds ?? [], "LENGTH"),
    width_mm: measurementBase(data, reference.kinds ?? [], "WIDTH"),
    height_mm: measurementBase(data, reference.kinds ?? [], "HEIGHT"),
  });

  const tabs: EditPaneTab[] = [
    {
      id: "general",
      label: t("part.tab.general", "General"),
      content: (
        <PartGeneralTab
          draft={draft}
          onChange={setDraft}
          readOnly={readOnly}
          groups={reference.groups ?? []}
          units={reference.units ?? []}
          departments={reference.departments ?? []}
          edgebands={data.edgebands}
        />
      ),
    },
    {
      id: "stock",
      flush: true,
      label: t("part.tab.stock", "Stock"),
      content: <PartStockTab part={data.part} unitCode={savedStockUnit?.code ?? null} />,
    },
    // Where it is kept and how each place is replenished; a virtual part is never kept.
    ...(data.part.type === "VIRTUAL"
      ? []
      : [
          {
            id: "stocked-at",
            flush: true,
            label: t("part.tab.stocked-at", "Stocked at"),
            content: <PartStockedAtTab part={data.part} readOnly={readOnly} />,
          },
        ]),
    {
      id: "measurements",
      flush: true,
      label: t("part.tab.measurements", "Measurements"),
      badge: data.measurements.length,
      content: (
        <MeasurementsTab
          part={draft}
          rows={data.measurements}
          kinds={reference.kinds ?? []}
          units={reference.units ?? []}
          readOnly={readOnly}
          {...writeRelated(measurementClient)}
        />
      ),
    },
    // Edgebands, the bill and the routing are a made part's: a bought part has none.
    ...(data.part.type === "MANUFACTURED"
      ? [
          {
            id: "edgebands",
            flush: true,
            label: t("part.tab.edgebands", "Edgebands"),
            badge: data.edgebands.length,
            content: (
              <EdgebandsTab
                partId={data.part.id}
                rows={data.edgebands}
                readOnly={readOnly}
                lengthMm={measurementBase(data, reference.kinds ?? [], "LENGTH")}
                widthMm={measurementBase(data, reference.kinds ?? [], "WIDTH")}
                {...writeRelated(edgebandClient)}
              />
            ),
          },
        ]
      : []),
    {
      id: "units",
      flush: true,
      label: t("part.tab.units", "Units"),
      badge:
        data.units.length > 0 ? (
          data.units.length
        ) : stockUnit && stockUnit.base_factor === null ? (
          <Badge size="sm" variant="warning">
            !
          </Badge>
        ) : null,
      content: (
        <UnitsTab
          part={draft}
          rows={data.units}
          units={reference.units ?? []}
          suppliers={suppliers ?? []}
          stockUnit={stockUnit}
          readOnly={readOnly}
          {...writeRelated(unitClient)}
        />
      ),
    },
    {
      id: "suppliers",
      flush: true,
      label: t("part.tab.suppliers", "Suppliers"),
      badge: data.suppliers.length,
      content: (
        <SuppliersTab
          part={draft}
          rows={data.suppliers}
          suppliers={suppliers ?? []}
          units={reference.units ?? []}
          readOnly={readOnly}
          {...writeRelated(supplierLinkClient)}
        />
      ),
    },
    // Every order line of the part, open ones first in mind. Shown for every
    // type: a made part was sometimes bought, and its history says so.
    {
      id: "purchases",
      flush: true,
      label: t("part.tab.purchases", "Purchases"),
      content: <PartPurchasesTab part={data.part} units={reference.units ?? []} stockUnitCode={savedStockUnit?.code ?? null} />,
    },
    // A virtual panel has no bill of its own (bills consume it): its tab is the real panels it is cut from.
    // A bought part has no bill either (a subcontracted one has its Subcontract bill).
    ...(data.part.type === "VIRTUAL"
      ? [{
          id: "real-panels",
          flush: true,
          label: t("part.tab.real-panels", "Real panels"),
          content: (
            <VirtualPartRealParts
              partId={data.part.id}
              canWrite={canWrite}
              onDeleted={() => void navigate({ to: "/config/manufacturing/virtual-part" })}
            />
          ),
        }]
      : data.part.type === "MANUFACTURED"
        ? [{
          id: "bom",
          flush: true,
          label: t("part.tab.bom", "Bill of materials"),
          content: (
            <PartBomTab
              part={draft}
              units={reference.units ?? []}
              operations={reference.operations ?? []}
              departments={reference.departments ?? []}
              readOnly={readOnly}
            />
          ),
        }]
      : []),
    {
      id: "files",
      flush: true,
      label: t("part.tab.files", "Files"),
      content: <PartFilesPanel parts={[{ id: data.part.id, number: data.part.number }]} uploadPartId={data.part.id} readOnly={readOnly} />,
    },
    // What to buy and when, and what a subcontractor uses of ours: only for
    // parts that are bought. Keyed on the saved type, not the draft, so the
    // tabs do not flicker while someone edits the type.
    ...(data.part.type === "PURCHASED"
      ? [
          {
            id: "replenishment",
            flush: true,
            label: t("part.tab.replenishment", "Replenishment"),
            content: <PartReplenishmentTab part={data.part} readOnly={readOnly} />,
          },
          {
            id: "subcontract",
            flush: true,
            label: t("part.tab.subcontract", "Subcontract bill"),
            content: <PartSubcontractTab part={data.part} readOnly={readOnly} />,
          },
        ]
      : []),
    {
      id: "where-used",
      flush: true,
      label: t("part.tab.where-used", "Used in"),
      content: <PartWhereUsedTab part={draft} />,
    },
    {
      // Its colour and its sisters, the same part in the other colours (0071).
      id: "colours",
      flush: true,
      label: t("part.tab.colours", "Colours"),
      content: <PartColoursTab partId={data.part.id} readOnly={readOnly} />,
    },
    {
      id: "attributes",
      flush: true,
      label: t("part.tab.attributes", "Attributes"),
      badge: data.attribute_values.length || null,
      content: (
        <AttributesTab
          part={draft}
          values={data.attribute_values}
          attributes={reference.attributes ?? []}
          options={reference.attributeOptions ?? []}
          applicability={reference.attributeGroups ?? []}
          units={reference.units ?? []}
        />
      ),
    },
    {
      id: "notes",
      flush: true,
      label: t("part.tab.notes", "Notes"),
      content: <PartNotesTab partId={data.part.id} editable={!readOnly} />,
    },
  ];

  return (
    <>
      <PageHeader
        parent={{
          to: "/manufacturing/parts",
          label: t("navigation.item.manufacturing-parts", "Parts"),
          beforeLeave: leave,
        }}
        title={draft.number}
        subtitle={draft.name}
        badges={
          <>
            <Badge variant={draft.status === "ACTIVE" ? "success" : "error"} size="sm">
              {partStatusLabel(draft.status, t)}
            </Badge>
            <Badge variant={draft.type === "MANUFACTURED" ? "info" : draft.type === "VIRTUAL" ? "warning" : "neutral"} size="sm">
              {partTypeLabel(draft.type, t)}
            </Badge>
            {/* Ahead of the descriptive badges: when the bar runs out of room
                it clips from the end, and stock is what people look up most. */}
            <PartStockHeadline partId={data.part.id} unitCode={savedStockUnit?.code ?? null} />
            {group && (
              <Badge variant="neutral" size="sm">
                {group.code} · {group.name}
              </Badge>
            )}
            {size !== "" && (
              <Badge variant="neutral" size="sm">
                {size}
              </Badge>
            )}
            {version && (
              <Link to="/products/$productId" params={{ productId: String(version.output_product_id) }}>
                <Badge variant="info" size="sm">
                  {t("part.sells-as.version", "Sells as {{sku}} · assembly {{code}}", { sku: version.output_sku ?? version.output_product_id, code: version.code })}
                </Badge>
              </Link>
            )}
            {semiFinishedOf && (
              <Link to="/products/$productId" params={{ productId: String(semiFinishedOf.product_id) }}>
                <Badge variant="info" size="sm">
                  {t("part.semi-finished-of", "Semi-finished of {{sku}}", { sku: semiFinishedOf.sku })}
                </Badge>
              </Link>
            )}
            {dirty && (
              <Badge variant="warning" size="sm">
                {t("part.unsaved", "Unsaved")}
              </Badge>
            )}
          </>
        }
        actions={
          canWrite ? (
            <Button
              variant="primary"
              disabled={!dirty || incomplete}
              title={dirty && incomplete ? t("part.save.incomplete", "Fix the fields marked on the General tab first.") : undefined}
              loading={save.isPending}
              onClick={() => save.mutate(draft)}
            >
              {t("button.save", "Save")}
            </Button>
          ) : null
        }
      />

      <EditPane tabs={tabs} urlParam="tab" />
    </>
  );
}

/** The base value of one measurement kind, by code rather than by id. */
function measurementBase(
  detail: { measurements: Array<{ measurement_kind_id: number; value_base?: number | null }> },
  kinds: MeasurementKindDTO[],
  code: string,
): number | null {
  const kind = kinds.find((entry) => entry.code === code);
  if (!kind) return null;
  const row = detail.measurements.find((entry) => entry.measurement_kind_id === kind.id);
  return row?.value_base ?? null;
}

/**
 * The reference lists every part page needs.
 *
 * Half-hour stale time: these are settings tables, edited a few times a year,
 * and refetching them per part would be seven requests for data that has not
 * moved since login. They are separate queries rather than one bundle endpoint
 * so that every page which needs "the unit list" shares the same cache entry.
 */
export function useReference(http: HttpClient, enabled: boolean) {
  const options = { enabled, staleTime: 30 * 60_000 };

  const groups = useQuery({
    queryKey: ["manufacturing-part-group", "all"],
    queryFn: () => new ResourceClient<PartGroupDTO>(http, "/manufacturing/part-group").findAll(),
    ...options,
  });
  const units = useQuery({
    queryKey: ["unit", "all"],
    queryFn: () => new ResourceClient<UnitDTO>(http, "/unit").findAll(),
    ...options,
  });
  const kinds = useQuery({
    queryKey: ["measurement-kind", "all"],
    queryFn: () => new ResourceClient<MeasurementKindDTO>(http, "/measurement-kind").findAll(),
    ...options,
  });
  const departments = useQuery({
    queryKey: ["department", "all"],
    queryFn: () => new ResourceClient<DepartmentDTO>(http, "/department").findAll(),
    ...options,
  });
  const attributes = useQuery({
    queryKey: ["attribute", "all"],
    queryFn: () => new ResourceClient<AttributeDTO>(http, "/attribute").findAll(),
    ...options,
  });
  const attributeOptions = useQuery({
    queryKey: ["attribute-option", "all"],
    queryFn: () => new ResourceClient<AttributeOptionDTO>(http, "/attribute-option").findAll(),
    ...options,
  });
  const operations = useQuery({
    queryKey: ["operation", "all"],
    queryFn: () => new ResourceClient<OperationDTO>(http, "/operation").findAll(),
    ...options,
  });
  const attributeGroups = useQuery({
    queryKey: ["part-attribute-group", "all"],
    queryFn: () => new ResourceClient<PartAttributeGroupDTO>(http, "/part-attribute-group").findAll(),
    ...options,
  });

  return {
    groups: groups.data,
    units: units.data,
    kinds: kinds.data,
    departments: departments.data,
    attributes: attributes.data,
    attributeOptions: attributeOptions.data,
    operations: operations.data,
    attributeGroups: attributeGroups.data,
  };
}
